import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  GoLocation,
  GoHome,
  GoPerson,
  GoPeople,
  GoLog,
  GoArrowSwitch,
  GoNote,
  GoOrganization,
} from "react-icons/go";
import { LuCircleUserRound, LuMapPinned, LuNotebookPen } from "react-icons/lu";
import { GrBraille, GrGroup } from "react-icons/gr";
import logo from "../assets/nav/logo.png";
import recycleLogo from "../assets/nav/recycle-logo.png";
import useUserStore2 from "../stores/userStore2";
import { RoleAccess } from "../auth/role-access";
import useAppModeStore from "../stores/useAppModeStore";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { cn } from "../lib/utils";

export const createMenuItem = (name, path, icon, extraProps = {}) => {
  return {
    name,
    path,
    icon: React.cloneElement(icon, { className: "h-6 w-6 mr-3" }),
    ...extraProps,
  };
};

const CombinedSidebar = ({ isMobileOpen, toggleMobileSidebar }) => {
  const navigate = useNavigate();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const { user } = useUserStore2();
  const { mode, setMode } = useAppModeStore();

  // Set default mode based on role when component mounts
  useEffect(() => {
    if (user?.role) {
      // Roles that should default to work mode
      const workModeRoles = ["Super Admin", "POC Attendant", "Collector"];
      if (workModeRoles.includes(user.role)) {
        setMode("work");
      } else {
        setMode("member");
      }
    }
  }, [user?.role, setMode]);

  const pathsWithoutId = [
    // "/ca/pending",
    "/pending-requests",
    // "/group-management",
    // Add other paths that shouldn't have IDs
  ];

  const roleMappings = {
    U: "User",
    CA: "Community Admin",
    GL: "Group Leader",
    SA: "Super Admin",
    POCA: "POC Attendant",
    C: "Collector",
  };

  // Define menu items configuration with paths only (no separate resource property)
  const menuItemsConfig = [
    // Dashboards
    { name: "Dashboard", path: "/dashboard", icon: <GoHome /> },
    { name: "My Dashboard", path: "/member-dashboard", icon: <GoHome /> },
    {
      name: "Community Dashboard",
      path: "/comm-dashboard",
      icon: <GoOrganization />,
    },

    // User and members management
    { name: "User Accounts", path: "/users", icon: <GoPerson /> },
    {
      name: "Members",
      path: "/member-management",
      icon: <LuCircleUserRound />,
    },

    // Community related
    { name: "Communities", path: "/communities", icon: <GoPeople /> },
    {
      name: "All Communities",
      path: "/community-management",
      icon: <GoPeople />,
    },

    // Group management
    { name: "Groups", path: "/group-management", icon: <GrGroup /> },

    // POC related
    { name: "POC", path: "/poc", icon: <LuMapPinned /> },
    { name: "Collections", path: "/pocattendant", icon: <GoLocation /> },
    { name: "Collections", path: "/poccollector", icon: <GoLocation /> },

    // Material management
    { name: "Materials", path: "/material-management", icon: <GrBraille /> },
    { name: "Materials", path: "/materials", icon: <GrBraille /> },

    // Requests
    { name: "Requests", path: "/pending-requests", icon: <GoLog /> },
    { name: "Requests", path: "/ca/pending", icon: <GoLog /> },
    {
      name: "Requests",
      path: "/group-admin",
      icon: <LuNotebookPen />,
      idType: "groupId", // Specify which ID to use
    },

    // Transactions
    { name: "Transactions", path: "/transactions", icon: <GoArrowSwitch /> },

    // News
    { name: "News", path: "/news", icon: <GoNote /> },
  ];

  // Items that should appear under each community
  const communityMenuItemsConfig = [
    // Dashboards

    {
      name: "Community Dashboard",
      path: "/comm-dashboard",
      icon: <GoOrganization />,
    },

    // User and members management
    { name: "User Accounts", path: "/users", icon: <GoPerson /> },
    {
      name: "Members",
      path: "/member-management",
      icon: <LuCircleUserRound />,
    },

    // Community related
    { name: "Communities", path: "/communities", icon: <GoPeople /> },

    // Group management
    { name: "Groups", path: "/group-management", icon: <GrGroup /> },

    // POC related
    { name: "POC", path: "/poc", icon: <LuMapPinned /> },
    { name: "Collections", path: "/pocattendant", icon: <GoLocation /> },
    { name: "Collections", path: "/poccollector", icon: <GoLocation /> },

    // Material management
    { name: "Materials", path: "/material-management", icon: <GrBraille /> },

    // Requests
    { name: "Requests", path: "/pending-requests", icon: <GoLog /> },
    { name: "Requests", path: "/ca/pending", icon: <GoLog /> },
    {
      name: "Requests",
      path: "/group-admin",
      icon: <LuNotebookPen />,
      useGroupId: true, // Add this flag
    },

    // Transactions
    { name: "Transactions", path: "/transactions", icon: <GoArrowSwitch /> },
  ];

  const role = user?.role ? roleMappings[user.role] || user.role : null;

  // Get membershipRole from user's current profile
  const membershipRole = user?.currentProfile?.role;

  const mappedMembershipRole = membershipRole
    ? roleMappings[membershipRole] || membershipRole
    : null;
  const accessRole = mode === "work" ? role : mappedMembershipRole;
  const userAccess = new RoleAccess([accessRole]);

  // If no role exists in either mode, show login prompt
  if (!role && !mappedMembershipRole) {
    return (
      <>
        {isMobileOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={toggleMobileSidebar}
          ></div>
        )}

        <div
          className={`p-4 md:m-7 md:mr-2 pr-1 fixed md:static top-0 left-0 z-50 md:h-[calc(100vh-6rem)] h-full bg-white shadow-lg transform transition-transform md:rounded-2xl flex flex-col justify-between ${
            isMobileOpen ? "translate-x-0" : "-translate-x-[110%]"
          } md:translate-x-0 md:flex md:flex-col md:justify-between ${
            isCollapsed ? "w-20 min-w-[5rem]" : "w-64 min-w-[16rem]"
          }`}
          style={{ marginBottom: "1rem" }}
        >
          <div className="flex flex-col justify-between h-full transition-all">
            <div>
              <div className="flex items-center justify-between mb-8">
                <img
                  src={isCollapsed ? recycleLogo : logo}
                  alt="Logo"
                  className={`h-10 ${isCollapsed ? "mx-auto" : ""}`}
                />
                <GoHome
                  className={`cursor-pointer w-6 h-6 md:hidden ${
                    isCollapsed ? "hidden" : "block"
                  }`}
                  onClick={toggleMobileSidebar}
                />
              </div>

              {!isCollapsed && (
                <div className="text-base text-gray-400 mb-5">MAIN MENU</div>
              )}

              {/* Login prompt */}
              <div className="p-4">
                <p className="text-gray-600 mb-4">
                  You are currently not logged in.
                </p>
                <Link
                  to="/login"
                  className="w-full bg-[#14B8A6] text-white py-2 px-4 rounded-lg hover:bg-[#0D9488] transition-colors flex items-center justify-center"
                >
                  Login Here
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Filter menu items for work mode
  const workMenuItems = menuItemsConfig
    .filter((config) => {
      const resource = config.path.substring(1);
      return userAccess.can("view", resource);
    })
    .map((config) => createMenuItem(config.name, config.path, config.icon));

  const memberships = user.currentProfile?.memberships || [];

  // Update the membershipsWithMenuItems mapping to include community-specific items
  const membershipsWithMenuItems = memberships.map((membership) => {
    // Map the role code to full name
    const mappedRole = roleMappings[membership.role] || membership.role;

    const roleAccess = new RoleAccess([mappedRole]);

    // Main accessible menu items (outside community)
    const accessibleMenuItems = menuItemsConfig
      .filter((config) => {
        const resource = config.path.substring(1);
        return roleAccess.can("view", resource);
      })
      .map((config) => createMenuItem(config.name, config.path, config.icon));

    // Community-specific menu items
    const communityMenuItems = communityMenuItemsConfig
      .filter((config) => {
        const resource = config.path.substring(1);
        return roleAccess.can("view", resource);
      })
      .map((config) =>
        createMenuItem(
          config.name,
          config.path,
          config.icon,
          { useGroupId: config.useGroupId } // Pass through the flag
        )
      );

    return {
      ...membership,
      role: mappedRole,
      accessibleMenuItems,
      communityMenuItems,
    };
  });

  return (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleMobileSidebar}
        ></div>
      )}

      <div
        className={`p-4 md:m-7 md:mr-2 pr-1 fixed md:static top-0 left-0 z-50 md:h-[calc(100vh-6rem)] h-full bg-white shadow-lg transform transition-transform md:rounded-2xl flex flex-col justify-between ${
          isMobileOpen ? "translate-x-0" : "-translate-x-[110%]"
        } md:translate-x-0 md:flex md:flex-col md:justify-between ${
          isCollapsed ? "w-20 min-w-[5rem]" : "w-64 min-w-[16rem]"
        }`}
        style={{ marginBottom: "1rem" }}
      >
        <div className="flex flex-col justify-between h-full transition-all">
          <div>
            <div className="flex items-center justify-between mb-8">
              <img
                src={isCollapsed ? recycleLogo : logo}
                alt="Logo"
                className={`h-10 ${isCollapsed ? "mx-auto" : ""}`}
              />
              <GoHome
                className={`cursor-pointer w-6 h-6 md:hidden ${
                  isCollapsed ? "hidden" : "block"
                }`}
                onClick={toggleMobileSidebar}
              />
            </div>

            {isCollapsed && (
              <div className="flex justify-center mb-4">
                <GoHome
                  className="cursor-pointer w-6 h-6"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                />
              </div>
            )}

            {!isCollapsed && (
              <div className="text-base text-gray-400 mb-5">MAIN MENU</div>
            )}

            {/* Work mode pages */}
            {mode === "work" && (
              <nav>
                <ul className="space-y-0">
                  {workMenuItems.map((item, index) => (
                    <li
                      key={index}
                      className={`rounded-lg w-full ${
                        location.pathname === item.path ? "bg-[#F0FDFA]" : ""
                      }`}
                      onMouseEnter={() => setHoveredItem(item.name)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      <Link
                        to={item.path}
                        className={`text-base flex items-center w-full px-4 py-3 ${
                          location.pathname === item.path
                            ? "text-[#14B8A6]"
                            : "text-gray-500 hover:text-[#14B8A6]"
                        } ${isCollapsed ? "justify-center" : ""}`}
                      >
                        {item.icon}
                        {!isCollapsed && (
                          <span className="flex-1">{item.name}</span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            {/* Not Work mode pages - only show if not SA and not in work mode */}
            {mode !== "work" &&
              role !== "SA" &&
              membershipsWithMenuItems.length > 0 && (
                <>
                  {/* Top-level menu items (outside communities) */}
                  <nav>
                    <ul className="space-y-0 mb-4">
                      {membershipsWithMenuItems[0]?.accessibleMenuItems
                        .filter((item) =>
                          [
                            "My Dashboard",
                            "All Communities",
                            "Materials",
                            "News",
                          ].includes(item.name)
                        )
                        .map((item, index) => (
                          <li
                            key={`top-${index}`}
                            className={`rounded-lg w-full ${
                              location.pathname === item.path
                                ? "bg-[#F0FDFA]"
                                : ""
                            }`}
                            onMouseEnter={() => setHoveredItem(item.name)}
                            onMouseLeave={() => setHoveredItem(null)}
                          >
                            <Link
                              to={item.path}
                              className={`text-base flex items-center w-full px-4 py-3 ${
                                location.pathname === item.path
                                  ? "text-[#14B8A6]"
                                  : "text-gray-500 hover:text-[#14B8A6]"
                              } ${isCollapsed ? "justify-center" : ""}`}
                            >
                              {item.icon}
                              {!isCollapsed && (
                                <span className="flex-1">{item.name}</span>
                              )}
                            </Link>
                          </li>
                        ))}
                    </ul>
                  </nav>

                  {/* Communities section */}
                  <div className="text-base text-gray-400 mb-2">
                    COMMUNITIES
                  </div>
                  <Accordion type="single" collapsible className="w-full">
                    {membershipsWithMenuItems.map((membership, index) => (
                      <AccordionItem
                        key={index}
                        value={`item-${index}`}
                        className="border-b-0"
                      >
                        <AccordionTrigger
                          className={cn(
                            "px-4 py-3 text-left text-base text-gray-500",
                            "hover:text-[#14B8A6] hover:bg-[#F0FDFA]",
                            "rounded-lg data-[state=open]:bg-[#F0FDFA]",
                            "w-full no-underline hover:no-underline",
                            "group" // Add group for targeting the chevron
                          )}
                        >
                          <div className="flex w-full items-center">
                            <div className="flex-1 flex flex-col">
                              <div className="flex items-center">
                                {membership.communityName ||
                                  "Unnamed Community"}
                              </div>
                              <span className="text-teal-500 border border-teal-500 rounded-full px-1.5 mt-1 text-xs self-start">
                                {membership.role}
                              </span>
                            </div>
                          </div>
                        </AccordionTrigger>

                        <AccordionContent>
                          {membership.communityMenuItems.length > 0 ? (
                            <ul className="space-y-0">
                              {membership.communityMenuItems.map(
                                (item, idx) => {
                                  const idToUse = item.useGroupId
                                    ? membership.groupId
                                    : membership.communityId;

                                  // Check if this path should have an ID appended
                                  const finalPath = pathsWithoutId.includes(
                                    item.path
                                  )
                                    ? item.path
                                    : `${item.path}/${idToUse}`;

                                  return (
                                    <li
                                      key={idx}
                                      className={`rounded-lg w-full pl-4 ${
                                        location.pathname === finalPath
                                          ? "bg-[#F0FDFA]"
                                          : ""
                                      }`}
                                    >
                                      <Link
                                        to={finalPath}
                                        className={`text-base flex items-center w-full px-4 py-3 ${
                                          location.pathname === finalPath
                                            ? "text-[#14B8A6]"
                                            : "text-gray-500 hover:text-[#14B8A6]"
                                        }`}
                                      >
                                        {item.icon}
                                        <span className="flex-1">
                                          {item.name}
                                        </span>
                                      </Link>
                                    </li>
                                  );
                                }
                              )}
                            </ul>
                          ) : (
                            <div className="ml-6 px-4 py-2 text-sm text-gray-400 italic">
                              No accessible pages.
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </>
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CombinedSidebar;
